import {
  checklistPaymentAmount,
  checklistAttachedDocuments,
  checklistMissingReasons
} from '~/features/checklists/selectors'
import type { STProcessMap } from '~/store-modules'
import { currentUser, logout, type AuthModuleState } from '../auth/auth-module'
import { clientFolderLoggedOutRoute } from '../routes'
import type { STChecklistItem, STMissingReason, IncompleteReasonOption } from '@st/sdk'
import type { FolderQuestionnaireSubmitDialogState } from '../st-folder-viewer/st-folder-questionnaire-types'
import { defSelector, type AppState } from '@st/redux'
import { match, P } from '@st/pattern'
import { formatISO8601Date, formatISO8601Time } from '@st/util/time'
import { contractCardSource } from '../contract-signing/contract-signing-shell-module'
import {
  selClientPerspectiveDocumentChecklistItems,
  selClientPerspectiveChecklistItems,
  selFolderDocuments,
  selClientPerspectiveDocuments,
  selClientPerspectiveDocumentsUnlinkedToChecklistItems,
  selFolderMissingReasonOptionsByConstraintId,
  orderedOrganizerSections,
  folderInvoice,
  invoicePaidLabel,
  selFolderState,
  selFolderReadyState,
  selQuestionnaireDashboard
} from '../st-folder-viewer/st-folder-selectors'

export const selClientTaxPayments = defSelector([selFolderReadyState], (ready) => {
  if (!ready) return null
  const state = ready.folderState
  const checklistItems = selClientPerspectiveChecklistItems(ready)
  const documents = selFolderDocuments(ready)
  const items = checklistItems.flatMap((item) =>
    match(item)
      .with({ phase: 'delivery', context: { type: 'tax_payment' } }, (item) => [
        {
          ...item,
          documents: checklistAttachedDocuments(item, documents),
          amountLabel: checklistPaymentAmount(item),
          dueLabel: match(item.dueOn)
            .with(P.nullish, () => undefined)
            .otherwise((date) => formatISO8601Date(date, 'UTC'))
        }
      ])
      .otherwise(() => [])
  )
  if (items.length == 0) return null
  return {
    archived: state.folder.archived,
    completed: items.every((item) => item.status == 'complete'),
    items
  }
})

type OrganizerStep =
  | {
      kind: 'contract'
      id: string
      contract: ReturnType<typeof contractCardSource>
      paymentRequired: boolean
    }
  | {
      kind: 'invoice'
      id: string
      url: string
      paidLabel: string | undefined
      archived: boolean
    }
  | { kind: 'questionnaire'; id: string; locked: boolean }
  | { kind: 'documents'; id: string }
  | {
      kind: 'submit'
      id: string
      canSubmit: boolean
      label: string
      status: string
      completed: boolean
    }
  | { kind: 'payments'; id: string; model: NonNullable<ReturnType<typeof selClientTaxPayments>> }

export const selClientOrganizerDashboard = defSelector(
  [selFolderState, selQuestionnaireDashboard, selClientTaxPayments],
  (folderState, dashboard, taxPayments) => {
    if (!folderState) return null
    const {
      folder,
      clientOrganizerSettings,
      engagementLetter,
      taxReturnDelivery,
      payInvoice,
      documents
    } = folderState
    const archived = folder.archived
    const submitted = folder.clientOrganizer.status == 'submitted'
    const documentUploadsEnabled = clientOrganizerSettings.sections.documents.enabled
    const showSubmit =
      clientOrganizerSettings.sections.questionnaire.enabled || documentUploadsEnabled
    const sections = orderedOrganizerSections(clientOrganizerSettings).filter(
      ([, section]) => section.enabled
    )
    // Visibility, order, and numbering share one source; cards receive ready payloads.
    const earlier: OrganizerStep[] = sections.map(([id, section]) =>
      match(section.type)
        .returnType<OrganizerStep>()
        .with('engagement_letter', () => ({
          kind: 'contract' as const,
          id,
          contract: contractCardSource(engagementLetter, documents),
          paymentRequired: false
        }))
        .with('questionnaire', () => ({ kind: 'questionnaire', id, locked: archived || submitted }))
        .with('documents', () => ({ kind: 'documents', id }))
        .exhaustive()
    )
    if (showSubmit)
      earlier.push({
        kind: 'submit',
        id: 'submit',
        canSubmit: !archived && !submitted && dashboard.status == 'idle',
        label: match({ submitted, status: dashboard.status })
          .with({ submitted: true }, () => 'Submitted')
          .with({ status: 'finishing' }, () => 'Submitting...')
          .otherwise(() => 'Submit'),
        status: match({ submitted, submittedAt: folder.clientOrganizer.submittedAt })
          .with(
            { submitted: true, submittedAt: P.string },
            ({ submittedAt }) =>
              `Submitted on ${formatISO8601Date(submittedAt)} at ${formatISO8601Time(submittedAt)}`
          )
          .otherwise(
            () =>
              'Once you complete the items above, submit the organizer to let us know you are finished.'
          ),
        completed: submitted
      })
    const taxReturn: OrganizerStep[] = []
    const invoice = folderInvoice(payInvoice)
    // The invoice belongs to the delivered return, so staff can attach it
    // before publishing without the client seeing it early.
    if (invoice && taxReturnDelivery)
      taxReturn.push({
        kind: 'invoice',
        id: 'invoice',
        url: invoice.url,
        paidLabel: match(invoice.paidAt)
          .with(P.string, invoicePaidLabel)
          .otherwise(() => undefined),
        archived
      })
    if (taxReturnDelivery)
      taxReturn.push({
        kind: 'contract',
        id: 'signing',
        contract: contractCardSource(taxReturnDelivery, documents),
        // Mirrors the backend's submission check: signing waits; review does not.
        paymentRequired: match(invoice)
          .with({ required: true, status: 'unpaid' }, () => true)
          .otherwise(() => false)
      })
    if (taxPayments) taxReturn.push({ kind: 'payments', id: 'payments', model: taxPayments })
    const steps = [...earlier, ...taxReturn].map((step, index) => ({
      ...step,
      stepNumber: index + 1
    }))
    const banner = match({
      archived,
      submitted,
      showSubmit,
      questionnaireCompleted: folder.clientOrganizer.questionnaireCompleted
    })
      .with({ archived: true }, () => 'archived' as const)
      .with({ submitted: true, showSubmit: true }, () => 'submitted' as const)
      .with(
        { showSubmit: true, questionnaireCompleted: true },
        () => 'questionnaireCompleted' as const
      )
      .otherwise(() => 'none' as const)
    return {
      earlierSteps: steps.slice(0, earlier.length),
      taxReturnSteps: steps.slice(earlier.length),
      banner,
      documentUploadsEnabled,
      isArchived: archived,
      groupEarlierSteps: sections.length > 0 && taxReturnDelivery != null
    }
  }
)

export function selectSubmitOrganizerDialog(
  dialog: FolderQuestionnaireSubmitDialogState,
  documentUploadsEnabled: boolean,
  documents: STChecklistItem[],
  otherItems: STChecklistItem[],
  reasons: Record<string, STMissingReason[]>,
  selectedReasons: Record<string, IncompleteReasonOption>
) {
  if (dialog.status == 'validating') return { items: [], subtitle: '' }
  const items = [...documents, ...otherItems]
    .filter(
      (item) =>
        dialog.validationResult.validations.find(
          (validation) => validation.checklistItemId == item.id
        )?.status == 'requires_missing_reason'
    )
    .map((item) => ({
      ...item,
      incompleteReasonOptions: checklistMissingReasons(item.context, reasons),
      requiresReason: dialog.submissionCount > 0 && !selectedReasons[item.id]
    }))
  const subtitle = match({ status: dialog.validationResult.status, documentUploadsEnabled })
    .with(
      { status: 'missing_reasons_error' },
      () =>
        "You're still missing some items we expected to receive from you. To submit without them, please let us know why below."
    )
    .with(
      { documentUploadsEnabled: true },
      () => 'Please make sure you have most of your documents uploaded before submitting.'
    )
    .otherwise(() => 'Please review your information before submitting.')
  return { items, subtitle }
}

export function selClientOrganizerLogout(
  auth: AuthModuleState,
  folderState: ReturnType<typeof selFolderState>
) {
  if (!folderState || currentUser(auth)?.provider != 'karbon') return logout()
  // Leave the organizer before Karbon logout to prevent automatic OAuth login.
  return logout({
    type: 'path',
    pathname: clientFolderLoggedOutRoute({
      folderId: folderState.folder.id,
      organizationSlug: folderState.organization.slug
    }),
    replace: true,
    beforeLogout: true
  })
}

export const selClientUploadDocuments = defSelector(
  ['stFolder', 'documentUploads'],
  ({ stFolder, documentUploads }: Pick<AppState<STProcessMap>, 'stFolder' | 'documentUploads'>) => {
    return match(stFolder)
      .with({ status: P.union('idle', 'loading', 'unauthorized') }, ({ status }) => ({ status }))
      .with({ status: 'ready' }, (stFolder) => {
        const items = selClientPerspectiveDocumentChecklistItems(stFolder)
        const documents = selClientPerspectiveDocuments(stFolder)
        const reasons = selFolderMissingReasonOptionsByConstraintId(stFolder)
        const uploads = Object.values(documentUploads.uploads)
        const dialog = match(stFolder.clientUploadDialog)
          .with({ type: 'setIncompleteReason' }, (dialog) => ({
            ...dialog,
            options: checklistMissingReasons(dialog.checklistItem.context, reasons),
            cancelLabel: match(dialog.checklistItem.incompleteReason != undefined)
              .with(true, () => 'Remove reason')
              .with(false, () => 'Cancel')
              .exhaustive()
          }))
          .otherwise((dialog) => dialog)
        return {
          status: 'ready' as const,
          folder: stFolder.folderState.folder,
          dialog,
          checklistCompleted:
            items.length > 0 && items.every((item) => item.status == 'complete' || item.cancelled),
          checklistRows: items.map((item) => ({
            item,
            documents: checklistAttachedDocuments(item, documents),
            incompleteReason: checklistMissingReasons(item.context, reasons).find(
              (option) => option.key == item.incompleteReason
            ),
            upload: uploads.find((upload) => upload.checklistItemId == item.id)
          })),
          otherDocuments: selClientPerspectiveDocumentsUnlinkedToChecklistItems(stFolder),
          otherUploads: uploads.filter((upload) => upload.checklistItemId == null),
          otherDocumentsTitle: match(items.length > 0)
            .with(true, () => 'Other documents')
            .with(false, () => 'Documents')
            .exhaustive(),
          instructions: match(items.length > 0)
            .with(false, () => 'Upload your documents below.')
            .with(
              true,
              () =>
                'Upload your documents to the matching items below. If you don\'t see a match, upload them to "Other Documents" at the bottom.'
            )
            .exhaustive()
        }
      })
      .exhaustive()
  }
)

export type ClientUploadDocumentsModel = Extract<
  ReturnType<typeof selClientUploadDocuments>,
  { status: 'ready' }
>
